/**
 * Frame-rate-independent flight model (FS-05 §4.1). Integrated at the fixed step; semi-implicit Euler
 * in the normative order: acceleration → clamp speed → exponential damping → integrate → playfield
 * clamp → soft wall → bank. No term is scaled by a variable frame delta.
 */
import type { MutableInputFrame } from './input';
import type { ResolvedModifiers } from './modifiers';
import type { TunnelWorld, Vec2Out } from '../game/tunnel-world';
import { clamp } from '../core/math-det';

export interface ShipState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  bank: number;
  bankVel: number;
  iframes: number; // s
  hp: number;
  prevX: number;
  prevY: number;
}

const BANK_SPRING_K = 110; // /s², critically damped (FS-05 §5.7)
const WALL_BLEED = 9.0; // /s
const WALL_RESTORE = 22; // u/s² per u

const _clampOut: Vec2Out = { x: 0, y: 0 };
const _normOut: Vec2Out = { x: 0, y: 0 };

export function createShipState(): ShipState {
  return { x: 0, y: 0, vx: 0, vy: 0, bank: 0, bankVel: 0, iframes: 0, hp: 1, prevX: 0, prevY: 0 };
}

export function resetShip(s: ShipState, hp: number): void {
  s.x = 0;
  s.y = 0;
  s.vx = 0;
  s.vy = 0;
  s.bank = 0;
  s.bankVel = 0;
  s.iframes = 0;
  s.hp = hp;
  s.prevX = 0;
  s.prevY = 0;
}

export function stepFlight(
  s: ShipState,
  input: MutableInputFrame,
  dt: number,
  mods: ResolvedModifiers,
  world: TunnelWorld,
): void {
  // 1. Acceleration (axis already unit-disc clamped).
  s.vx += input.ax * mods.lateralAccel * dt;
  s.vy += input.ay * mods.lateralAccel * dt;

  // 2. Clamp by magnitude.
  const sp = Math.hypot(s.vx, s.vy);
  if (sp > mods.maxLateralSpeed) {
    const k = mods.maxLateralSpeed / sp;
    s.vx *= k;
    s.vy *= k;
  }

  // 3. Exponential damping — frame-rate exact.
  const kd = Math.exp(-mods.damping * dt);
  s.vx *= kd;
  s.vy *= kd;

  // 4. Integrate, remembering previous position for the swept test.
  s.prevX = s.x;
  s.prevY = s.y;
  s.x += s.vx * dt;
  s.y += s.vy * dt;

  // 5. Playfield clamp (FS-03 owns geometry). Velocity is unaffected by clamping.
  world.clampToPlayfield(s.x, s.y, _clampOut);
  s.x = _clampOut.x;
  s.y = _clampOut.y;

  // 6. Soft wall — bleed outward velocity, no reflection.
  const pen = world.wallPenetration(s.x, s.y);
  if (pen > 0) {
    world.outwardNormal(s.x, s.y, _normOut);
    const vOut = s.vx * _normOut.x + s.vy * _normOut.y;
    if (vOut > 0) {
      const bleed = Math.exp(-WALL_BLEED * dt);
      s.vx -= _normOut.x * vOut * (1 - bleed);
      s.vy -= _normOut.y * vOut * (1 - bleed);
    }
    s.vx -= _normOut.x * WALL_RESTORE * pen * dt;
    s.vy -= _normOut.y * WALL_RESTORE * pen * dt;
  }

  // 7. Bank — presentation only. Critically damped spring toward a velocity-proportional target.
  const bankTarget = clamp(s.vx / mods.maxLateralSpeed, -1, 1) * mods.bankMax;
  const accel = BANK_SPRING_K * (bankTarget - s.bank) - 2 * Math.sqrt(BANK_SPRING_K) * s.bankVel;
  s.bankVel += accel * dt;
  s.bank += s.bankVel * dt;

  // i-frames decay.
  if (s.iframes > 0) {
    s.iframes -= dt;
    if (s.iframes < 0) s.iframes = 0;
  }
}
